import { ReactNode, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { HelpCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TooltipProps {
  title: string;
  content: string;
  children?: ReactNode;
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  showIcon?: boolean;
  persistent?: boolean;
}

export function Tooltip({ 
  title, 
  content, 
  children, 
  className, 
  side = 'bottom',
  showIcon = true,
  persistent = false
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {children || (
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-auto p-1", className)}
            onClick={() => setIsOpen(true)}
          >
            {showIcon && <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-primary" />}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent side={side} className="w-80 p-4" align="start">
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <h4 className="font-semibold text-sm">{title}</h4>
            {!persistent && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1 -mt-1 -mr-1"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {content}
          </p>
          {persistent && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                Got it
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}